import { useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  FILTER_HEADING as HEADING,
  FilterSidebarHeader,
} from "@/components/common/filter-sidebar-header/filter-sidebar-header";
import { CollapsibleSectionHeader } from "@/components/common/collapsible-section/collapsible-section-header";
import { useCollapsedSections } from "@/components/common/collapsible-section/use-collapsed-sections";
import { IssuePersonFilter } from "@/components/issues/issue-person-filter/issue-person-filter";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useProjectPeople } from "@/hooks/use-project-people";
import {
  EMPTY_ISSUE_FILTERS,
  activeFilterCount,
  type IssueRefinementFilters,
} from "@/lib/issue-filters";

interface IssueFilterSidebarProps {
  projectId: string;
  value: IssueRefinementFilters;
  onChange: (filters: IssueRefinementFilters) => void;
  /**
   * Offer Assigned to and Raised by. They list the project's members, so only
   * for someone who sees every issue. See [[issues-page.spec]] § Only the
   * issues you're on.
   */
  showPeople?: boolean;
}

/** Per browser; separate from the Photos sidebar, whose sections differ. */
const COLLAPSED_STORAGE_KEY = "otus:issue-filter-sidebar-collapsed";

/** The collapsible sections. Search and the Overdue switch always stay open. */
const PERSON_SECTIONS = [
  { id: "assigned-to", title: "Assigned to", field: "assignedTo" },
  { id: "raised-by", title: "Raised by", field: "createdBy" },
] as const;

/**
 * The desktop face of the issue filters, mirroring [[records-sidebar.spec]] on
 * the Photos page. Mobile uses [[issue-filter-drawer.spec]] instead.
 *
 * Unlike the drawer, changes here apply immediately: the sidebar sits beside
 * the list rather than covering it, so each change is visible where it lands
 * and an Apply button would only add a step. Search is the exception — it
 * commits on Enter, because a refetch per keystroke is several requests to type
 * one word. See [[issue-filter-sidebar.spec]].
 */
export function IssueFilterSidebar({
  projectId,
  value,
  onChange,
  showPeople = true,
}: IssueFilterSidebarProps) {
  const { people } = useProjectPeople(projectId);
  const { data: currentUser } = useCurrentUser();
  const { isCollapsed, toggle } = useCollapsedSections(COLLAPSED_STORAGE_KEY);

  const [searchInput, setSearchInput] = useState(value.search);
  const [searchSource, setSearchSource] = useState(value.search);

  // Mirror the applied search into the local input by adjusting state during
  // render rather than in an effect — the committed paint stays in sync with
  // the prop, with no flash of stale text when a filter is cleared elsewhere.
  if (searchSource !== value.search) {
    setSearchSource(value.search);
    setSearchInput(value.search);
  }

  const count = activeFilterCount(value);

  return (
    <aside className="hidden w-60 shrink-0 flex-col overflow-y-auto border-r border-border bg-panel md:flex">
      <FilterSidebarHeader
        count={count}
        onClearAll={() => onChange(EMPTY_ISSUE_FILTERS)}
      />

      <div className="border-b border-border px-5 py-3">
        <h3 className={`${HEADING} mb-2`}>Search</h3>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onChange({ ...value, search: searchInput.trim() });
          }}
        >
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="issue-search"
              type="search"
              enterKeyHint="search"
              value={searchInput}
              placeholder="Title or description"
              onChange={(event) => setSearchInput(event.target.value)}
              className="h-8 pl-8 pr-8 text-sm"
              aria-label="Search issues"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  onChange({ ...value, search: "" });
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
        <div className="flex flex-col">
          <span className="text-sm font-medium">Overdue only</span>
          <span className="text-xs text-muted-foreground">
            Past its due date and not yet closed
          </span>
        </div>
        <Switch
          checked={value.overdue}
          onCheckedChange={(checked) => onChange({ ...value, overdue: checked })}
          aria-label="Overdue only"
        />
      </div>

      {(showPeople ? PERSON_SECTIONS : []).map(({ id, title, field }) => {
        const collapsed = isCollapsed(id);
        const controlsId = `issue-filter-sidebar-${id}`;
        return (
          <div key={id} className="border-b border-border px-5 py-3">
            <CollapsibleSectionHeader
              title={title}
              collapsed={collapsed}
              onToggle={() => toggle(id)}
              controlsId={controlsId}
              activeCount={value[field] ? 1 : 0}
            />
            {!collapsed && (
              <div id={controlsId} className="mt-2">
                <IssuePersonFilter
                  title={title}
                  showTitle={false}
                  people={people}
                  currentUserId={currentUser?.id}
                  selected={value[field]}
                  onSelect={(personId) =>
                    onChange({ ...value, [field]: personId })
                  }
                />
              </div>
            )}
          </div>
        );
      })}
    </aside>
  );
}
